import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import "leaflet.awesome-markers";

export default class extends Controller {
  static targets = ["canvas"];

  static values = {
    offices: Array,
    mileInMeters: { type: Number, default: 1609.34 },
  };

  connect() {
    this.ukBounds = L.latLngBounds([49.5, -8.7], [59.5, 1.9]);

    this.map = L.map(this.canvasTarget, {
      zoomControl: false,
      attributionControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);

    L.control.zoom({
      position: "topleft",
      zoomInText: `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M10.75 5.75H5.75M5.75 5.75H0.75M5.75 5.75V0.75M5.75 5.75V10.75" stroke="#1F040F" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
      zoomOutText: `
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="2" viewBox="0 0 12 2" fill="none">
        <path d="M0 0.625C0 0.279822 0.279822 0 0.625 0H11.0417C11.3868 0 11.6667 0.279822 11.6667 0.625C11.6667 0.970178 11.3868 1.25 11.0417 1.25H0.625C0.279822 1.25 0 0.970178 0 0.625Z" fill="#1F040F"/>
      </svg>`,
    }).addTo(this.map);

    delete L.Icon.Default.prototype._getIconUrl;

    this.map.fitBounds(this.ukBounds, { padding: [20, 20] });

    this.shapesById = new Map();
    this._renderCoverage();

    this._onFocusOffice = (e) => this._focusOffice(e.detail);
    window.addEventListener("nova:offices:focusOffice", this._onFocusOffice);

    setTimeout(() => this.map.invalidateSize(), 0);
  }

  disconnect() {
    window.removeEventListener("nova:offices:focusOffice", this._onFocusOffice);

    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  _renderCoverage() {
    const offices = this.officesValue || [];
    this.coverageLayer = L.layerGroup().addTo(this.map);

    offices.forEach((office) => {
      const shape = this._buildShape(office);
      if (!shape) return;

      shape.addTo(this.coverageLayer);
      this.shapesById.set(String(office.id), shape);
    });

    this._fitToOffices();
  }

  _buildShape(office) {
    if (!Number.isFinite(office.lat) || !Number.isFinite(office.lng)) return null;

    const center = [office.lat, office.lng];

    if (office.coverage_type === "radius" && office.distance) {
      return L.circle(center, {
        color: "#7c3aed",
        fillColor: "#7c3aed",
        fillOpacity: 0.15,
        radius: office.distance * this.mileInMetersValue,
      }).bindTooltip(office.name);
    }

    if (office.coverage_type === "custom" && (office.polygon || []).length >= 3) {
      return L.polygon(office.polygon, {
        color: "#7c3aed",
        fillColor: "#7c3aed",
        fillOpacity: 0.15,
      }).bindTooltip(office.name);
    }

    return L.marker(center, { icon: this._officeIcon() }).bindTooltip(office.name);
  }

  _officeIcon() {
    return L.AwesomeMarkers.icon({ icon: "building", markerColor: "purple", prefix: "fa" });
  }

  _fitToOffices() {
    const points = (this.officesValue || [])
      .filter((office) => Number.isFinite(office.lat) && Number.isFinite(office.lng))
      .map((office) => [office.lat, office.lng]);

    if (!points.length) {
      this.map.fitBounds(this.ukBounds, { padding: [20, 20] });
      return;
    }

    this.map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 12 });
  }

  _focusOffice(detail) {
    if (!detail) return;

    const shape = this.shapesById.get(String(detail.id));
    if (!shape) return;

    if (typeof shape.getBounds === "function") {
      this.map.fitBounds(shape.getBounds(), { padding: [40, 40], maxZoom: 14 });
    } else if (typeof shape.getLatLng === "function") {
      this.map.flyTo(shape.getLatLng(), 14, { duration: 0.8 });
    }

    if (typeof shape.openTooltip === "function") shape.openTooltip();
  }
}
